import type {
  ExternalRunnerResponse,
  ExternalRunnerStatus,
  ReinstallReason,
} from '@/api/external-runners';

export const UNINSTALL_COMMAND = 'sudo sh mr-install.sh --uninstall';

export const EXTERNAL_STATUS_LABELS: Record<ExternalRunnerStatus, string> = {
  WAITING_FOR_INSTALL: 'Waiting for install',
  ONLINE: 'Online',
  OFFLINE: 'Offline',
  REVOKED: 'Not reporting',
};

const REINSTALL_REASONS: Record<ReinstallReason, string> = {
  CONFIG_CHANGED: 'its Monitoring or Metrics settings changed',
  AGENT_OUTDATED: 'its platform needs a newer agent',
  CREDENTIAL_REVOKED: 'its credential ended',
};

/** "8 cores / 16 threads" when cores and threads differ, otherwise "N CPUs"; null before a sample. */
export function cpuSummary(
  cpuLogical: number | null | undefined,
  cpuCores: number | null | undefined
): string | null {
  if (cpuLogical == null) return null;
  if (cpuCores != null && cpuCores !== cpuLogical) {
    return `${cpuCores} ${cpuCores === 1 ? 'core' : 'cores'} / ${cpuLogical} threads`;
  }
  return `${cpuLogical} ${cpuLogical === 1 ? 'CPU' : 'CPUs'}`;
}

/** Binary gigabytes, as the server's memory is reported in bytes. */
export function gigabytes(bytes: number | null | undefined): string | null {
  if (bytes == null) return null;
  const value = bytes / 1024 ** 3;
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} GiB`;
}

export function reinstallMessage(runner: Pick<ExternalRunnerResponse, 'reinstallReasons'>): string {
  const reasons = runner.reinstallReasons.map((reason) => REINSTALL_REASONS[reason]);
  if (reasons.length === 0) return '';
  const joined =
    reasons.length === 1 ? reasons[0] : `${reasons.slice(0, -1).join(', ')} and ${reasons.at(-1)}`;
  return `Reinstall needed: ${joined}. Run a new install command on the server.`;
}

/** Whether a new install command has something to install. */
export function installable(
  runner: Pick<ExternalRunnerResponse, 'monitoringEnabled' | 'metricsConnectionId'>
) {
  return runner.monitoringEnabled || !!runner.metricsConnectionId;
}
